import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Car, Loader2, Play, Square, Pencil, Pause } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { IRS_MILEAGE_RATE, MILEAGE_CATEGORY, mileageDeduction } from "@shared/expenses";
import { useTrip, startTrip, stopTrip, pauseTrip, resumeTrip, startManual, discardTrip, canBackgroundTrack } from "@/lib/tripStore";
import { BackgroundLocationDisclosure } from "@/components/BackgroundLocationDisclosure";

const LOCATION_DISCLOSURE_KEY = "stoop:bgLocationDisclosureSeen";

function hasSeenLocationDisclosure(): boolean {
  try {
    return window.localStorage.getItem(LOCATION_DISCLOSURE_KEY) === "1";
  } catch {
    return false;
  }
}

function markLocationDisclosureSeen(): void {
  try {
    window.localStorage.setItem(LOCATION_DISCLOSURE_KEY, "1");
  } catch {
    /* private mode — the disclosure just shows again next trip */
  }
}

export function MileageTracker() {
  const [showDisclosure, setShowDisclosure] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const trip = useTrip();
  const today = new Date().toISOString().slice(0, 10);

  const [editMiles, setEditMiles] = useState("");
  const [roundTrip, setRoundTrip] = useState(false);
  const [destination, setDestination] = useState("");

  useEffect(() => {
    if (trip.phase === "review") {
      setEditMiles(trip.miles > 0 ? trip.miles.toFixed(1) : "");
      setRoundTrip(false);
    }
    if (trip.phase === "idle") {
      setEditMiles("");
      setDestination("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip.phase]);

  const baseMiles = Math.max(0, Number(editMiles) || 0);
  const effectiveMiles = roundTrip ? baseMiles * 2 : baseMiles;
  const deduction = mileageDeduction(effectiveMiles);
  const background = canBackgroundTrack();

  const save = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/seller/expenses", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          expenseDate: today,
          category: MILEAGE_CATEGORY,
          vendor: destination.trim() || "Mileage",
          amount: deduction.toFixed(2),
          miles: effectiveMiles.toFixed(2),
          notes: `${effectiveMiles.toFixed(1)} mi${roundTrip ? " (round trip)" : ""} @ ${IRS_MILEAGE_RATE * 100}¢/mi`,
        }),
      });
      if (!res.ok) throw new Error((await res.text()) || "Could not save");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/seller/expenses"] });
      toast({ title: "Mileage logged", description: `${effectiveMiles.toFixed(1)} mi → $${deduction.toFixed(2)} deduction` });
      void discardTrip();
    },
    onError: (err: any) =>
      toast({ title: "Could not save", description: err?.message ?? "Try again", variant: "destructive" }),
  });

  return (
    <Card className="mb-4">
      <CardContent className="pt-4">
        <div className="mb-2 flex items-center gap-2">
          <Car className="h-5 w-5 text-primary" />
          <h2 className="text-sm font-semibold">Mileage tracker</h2>
        </div>

        {trip.phase === "idle" && (
          <>
            <p className="mb-3 text-xs text-muted-foreground">
              Tap start before you drive to a store or market. We track the miles by GPS and apply the{" "}
              {IRS_MILEAGE_RATE * 100}¢/mile IRS rate as a write-off, and auto-stop when you return to where you
              started.{" "}
              {background
                ? "Tracking keeps running in the background — a notification shows the whole time so you don't forget to stop it."
                : "Keep this screen open while you drive (it stays awake)."}
            </p>
            <Button
              className="w-full"
              onClick={() => {
                // The OS "Always" prompt fires inside startTrip(). Both stores
                // require our own disclosure to come first, and only the
                // background engine triggers it.
                if (background && !hasSeenLocationDisclosure()) {
                  setShowDisclosure(true);
                  return;
                }
                void startTrip();
              }}
            >
              <Play className="mr-2 h-4 w-4" />
              Start trip
            </Button>
            <BackgroundLocationDisclosure
              open={showDisclosure}
              onConfirm={() => {
                setShowDisclosure(false);
                markLocationDisclosureSeen();
                void startTrip();
              }}
              onDismiss={() => setShowDisclosure(false)}
            />
            <button
              type="button"
              onClick={startManual}
              className="mt-2 flex w-full items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <Pencil className="h-3 w-3" />
              Enter miles manually
            </button>
          </>
        )}

        {trip.phase === "tracking" && (
          <div className="text-center">
            <div className="text-4xl font-bold tabular-nums">{trip.miles.toFixed(1)}</div>
            <div className="text-xs text-muted-foreground">miles</div>
            <div className={`mt-1 flex items-center justify-center gap-1.5 px-2 text-[11px] ${trip.paused ? "text-muted-foreground" : "text-emerald-700"}`}>
              <span className={`h-2 w-2 shrink-0 rounded-full ${trip.paused ? "bg-muted-foreground" : "bg-emerald-500 animate-pulse"}`} />
              {trip.paused
                ? "Paused"
                : trip.error ?? (background ? "Tracking — you can switch to Maps; the notification controls it" : "Tracking — keep this screen open while you drive")}
            </div>
            <div className="mt-3 flex gap-2">
              {trip.paused ? (
                <Button variant="outline" className="flex-1" onClick={() => void resumeTrip()}>
                  <Play className="mr-2 h-4 w-4" />
                  Resume
                </Button>
              ) : (
                <Button variant="outline" className="flex-1" onClick={() => void pauseTrip()}>
                  <Pause className="mr-2 h-4 w-4" />
                  Pause
                </Button>
              )}
              <Button variant="destructive" className="flex-1" onClick={() => void stopTrip(false)}>
                <Square className="mr-2 h-4 w-4" />
                End trip
              </Button>
            </div>
            <button
              type="button"
              onClick={() => void discardTrip()}
              className="mt-2 text-xs text-muted-foreground hover:text-destructive"
            >
              Discard trip
            </button>
          </div>
        )}

        {trip.phase === "review" && (
          <div className="space-y-3">
            {trip.autoStopped && (
              <p className="rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200">
                Trip auto-stopped — you returned to your starting point. Miles below cover the full round trip.
              </p>
            )}
            <div>
              <Label htmlFor="trip-miles" className="text-xs">Miles driven</Label>
              <Input
                id="trip-miles"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.1"
                value={editMiles}
                onChange={(e) => setEditMiles(e.target.value)}
                placeholder="0.0"
                className="mt-1"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={roundTrip} onChange={(e) => setRoundTrip(e.target.checked)} className="h-4 w-4" />
              Round trip — double the miles (there and back)
            </label>
            <div>
              <Label htmlFor="trip-dest" className="text-xs">Destination (optional)</Label>
              <Input
                id="trip-dest"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                placeholder="e.g. Lowe's, farmers market"
                maxLength={200}
                className="mt-1"
              />
            </div>
            <div className="rounded-md bg-muted px-3 py-2 text-sm">
              {effectiveMiles.toFixed(1)} mi × {IRS_MILEAGE_RATE * 100}¢ ={" "}
              <span className="font-semibold">${deduction.toFixed(2)}</span> deduction
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => void discardTrip()} disabled={save.isPending}>
                Discard
              </Button>
              <Button
                className="flex-1"
                onClick={() => {
                  if (baseMiles <= 0) {
                    toast({ title: "Enter the miles driven", variant: "destructive" });
                    return;
                  }
                  save.mutate();
                }}
                disabled={save.isPending}
              >
                {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Log mileage
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
